export const clone = (obj: any) => JSON.parse(JSON.stringify(obj));
export const joinStrings = (
  str: string | string[],
  separator: string
): string => {
  if (typeof str === 'string') {
    return str;
  } else {
    return str.join(separator);
  }
};

/**
 * A utility function for building dispatchable functions.
 *
 * @param {Function} dispatch - The dispatch function provided by your Redux or state management library.
 * @returns {Function} A function that takes another function and dispatches its result.
 */
export const buildDispatchable = dispatch => {
  /**
   * Executes a given function and dispatches its result.
   *
   * @param {Function} fn - The function to be executed and its result dispatched.
   * @returns {Promise<any>} A promise that resolves to the result of the dispatched action.
   */
  return async fn => {
    try {
      return await dispatch(fn());
    } catch (e) {
      console.error('Error performing action', e);
    }
  };
};


/**
 * Extracts all ledger transactions from an invoice.
 * Since an invoice can contain multiple ledgers, this flattens them into a single list.
 */
export const getLedgerTransactionsFromInvoice = (
  invoice?:InvoicesAPI.Invoice | AmplifyAPI.Invoice
): CMxCommonApp.CaseLedgerTransactionDto[] => {
  return (
    invoice?.case_invoice_ledgers?.flatMap(
      (ledger) => ledger.case_ledger_transactions ?? []
    ) ?? []
  );
};

/**
 * Finds the ledger transactions corresponding to the given transaction guid.
 */
export const getLedgerTransaction = (
  ledgerTransactions: CMxCommonApp.CaseLedgerTransactionDto[],
  transactionGuid?: string
) => {
  if (!transactionGuid) {
    return undefined;
  }

  return ledgerTransactions.find(
  (ledgerTxn) =>
    ledgerTxn.transaction_guid?.uuid &&
    ledgerTxn.transaction_guid.uuid === transactionGuid
  );
};
/**
 * Determines if the transaction row should be highlighted.
 * A row highlight occurs when the transaction is newly introduced in the invoice.
 */
export const shouldHighlightTransaction = (
  txnLedger?: CMxCommonApp.CaseLedgerTransactionDto
) => {
  return txnLedger?.change_type === "INVOICE_CHARGE";
};

export const getLedgerChangedCodes = (
  txnLedger?: CMxCommonApp.CaseLedgerTransactionDto
) => {
  
  if (!txnLedger || txnLedger.change_type === "INVOICE_NO_CHANGE") {
    return {
      changedCptCodes: [],
      changedIcdCodes: [],
      changedModifiers: []
    };
  }

  const chargeCodes = txnLedger?.change_json?.charge_codes ?? {};

  return {
    changedCptCodes: [
      chargeCodes.cpt_codes?.ADDENDUM,
      chargeCodes.cpt_codes?.VOID
    ].filter(Boolean) as string[],

    changedIcdCodes: [
      ...(chargeCodes.icd_codes?.ADDENDUM ?? []),
      ...(chargeCodes.icd_codes?.VOID ?? [])
    ],

    changedModifiers: [
      ...(chargeCodes.modifiers?.ADDENDUM ?? []),
      ...(chargeCodes.modifiers?.VOID ?? [])
    ]
  };
};
const LEDGER_BANNER_CHANGE_TYPES = new Set([
  "INVOICE_CHARGE",
  "TRANSACTION_ADDENDUM",
  "TRANSACTION_VOID",
  "TRANSACTION_VOID_ADDENDUM"
]);

/**
 * Shared gate for both the ledger-update orange border and the reference-codes banner.
 * Both elements require the Code Sources feature flag AND that the case is a revision
 * (version > 1), since ledger changes are only meaningful relative to a prior version.
 */
export const isCodeSourcesLedgerUpdateEnabled = (
  codeSourcesOn: boolean,
  isRevisedCase: boolean
): boolean => codeSourcesOn && isRevisedCase;

/**
 * Determines if the process instance contains any ledger updates
 * that should trigger the reference codes update banner.
 */

export const hasLedgerUpdates = (
  processInstance?: AmplifyAPI.ProcessInstance
): boolean => {
  return (
    processInstance?.invoices?.some((inv) =>
      inv?.case_invoice_ledgers?.some((ledger) =>
        ledger?.case_ledger_transactions?.some(
          (txn) =>
            LEDGER_BANNER_CHANGE_TYPES.has(txn.change_type) &&
            !!txn.transaction_guid?.uuid
        )
      )
    ) ?? false
  );
};

const SOURCE_LABELS = {
    CLINICAL: "Source",
    MAPPED: "Rules",
    PREDICTION: "Prediction",
    PREDICTION_COMPONENT: "Prediction",
    ASSEMBLED: "Amplify"
  };

export const getInvoiceHighlightConfig = (
  invoiceType: string,
  ledgerTransactions = []
) => {
  const hasNew = ledgerTransactions.some(
    txn => txn.change_type === "INVOICE_CHARGE"
  );

  const hasAddendum = ledgerTransactions.some(
    txn =>
      txn.change_type === "TRANSACTION_ADDENDUM" ||
      txn.change_type === "TRANSACTION_VOID_ADDENDUM"
  );

  const hasVoid = ledgerTransactions.some(
    txn =>
      txn.change_type === "TRANSACTION_VOID" ||
      txn.change_type === "TRANSACTION_VOID_ADDENDUM"
  );

  const hasUpdate = hasAddendum || hasVoid;

  
  const label = SOURCE_LABELS[invoiceType] || invoiceType;

  //  NEW
  if (hasNew) {
    return {
      type: "NEW",
      className: "code-source-highlight-green",
      message: `The ${label} set is new since the case was coded.`
    };
  }

  //  UPDATED
  if (hasUpdate) {
    let changeSummary = "";

    if (hasAddendum && hasVoid) {
      changeSummary = "Charges have been added and removed.";
    } else if (hasVoid) {
      changeSummary = "A charge has been removed.";
    } else if (hasAddendum) {
      changeSummary = "A charge has been added.";
    }

    return {
      type: "UPDATED",
      className: "code-source-highlight-yellow",
      message: `The ${label} set has been updated. ${changeSummary}`
    };
  }

  return null;
};